import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance/axiosInstance";

import axios, { AxiosError, HttpStatusCode } from "axios";
import { UserLoginRequest } from "./UserLoginRequest";
import { UserLoginResponse } from "./UserLoginResponse";
import { UserRegisterRequest } from "./UserRegisterRequest";
import { RegisterErrorResponse } from "./RegisterErrorResponse";
import { clearAllCookies } from "../extensions/clearAllCookies";
import { ForgotPasswordRequest } from "./ForgotPasswordRequest";
import { ResetPasswordRequest } from "./ResetPasswordRequest";

export const registerUserAsync = createAsyncThunk<
  { registered: boolean },
  UserRegisterRequest,
  { rejectValue: RegisterErrorResponse }
>(
  "authentication/registerUser",
  async (userData: UserRegisterRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        message?: string;
      }>("/account/register", userData);
      if (response.status !== HttpStatusCode.Ok) {
        return rejectWithValue({
          status: response.status,
          message: response.data.message || "Registration failed",
        });
      }
      return { registered: true };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          return rejectWithValue({
            status: error.response.status as HttpStatusCode,
            message: "An unknown error occurred during registration.",
          });
        }
        return rejectWithValue({
          status: HttpStatusCode.InternalServerError,
          message: error.message,
        });
      }
      return rejectWithValue({
        status: HttpStatusCode.InternalServerError,
        message: "An unknown error occurred during registration",
      });
    }
  }
);

export const loginUserAsync = createAsyncThunk<
  UserLoginResponse,
  UserLoginRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/loginUser", async (userLoginRequest, { rejectWithValue }) => {
  try {
    clearAllCookies();
    const response = await axiosInstance.post<UserLoginResponse>(
      "/account/login",
      userLoginRequest
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Login failed",
      });
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Login error",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred during login",
    });
  }
});

export const resetPasswordAsync = createAsyncThunk<
  { success: boolean; message: string },
  string,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/resetPassword", async (email: string, { rejectWithValue }) => {
  try {
    const request: ForgotPasswordRequest = { email };
    const response = await axiosInstance.post<{ message: string }>(
      "/account/forgot-password",
      request
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Password reset request failed",
      });
    }

    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Password reset request failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unexpected error occurred during password reset request",
    });
  }
});

export const resetPasswordConfirmAsync = createAsyncThunk<
  { success: boolean; message: string },
  ResetPasswordRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/resetPasswordConfirm", async (request: ResetPasswordRequest, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<{ message: string }>(
      "/account/reset-password",
      request
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Password reset failed",
      });
    }

    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Password reset failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unexpected error occurred during password reset",
    });
  }
});
